<script setup lang="ts">
import debounce from 'lodash/debounce'

import BaseIcon from '~/components/common/BaseIcon.vue'

import ClientDebounce from '~/utils/client-debounce'
import { useTicker } from '~/composables/useTicker'
import { useTickerAnimation } from '~/composables/useTickerAnimation'
import { useAuthStore } from '~/stores/auth'

import type { TTickerData } from '~/server/types/ticker'
import { onClickOutside } from '@vueuse/core'

const { indexes: tickerIndexes, refreshTickerData } = useTicker()
const { portalUrl } = useRuntimeConfig().public
const { isAuthenticated, authChecked } = storeToRefs(useAuthStore())

const {
  startAnimation,
  resumeAnimation,
  pauseAnimation,
  moveToLeft,
  moveToRight,
  cleanupAnimation,
} = useTickerAnimation()

const isFirstIndexesContainerMounted = ref(false)
const isSecondIndexesContainerMounted = ref(false)
const debounceResumeAnimation = new ClientDebounce()
const indexes = computed<TTickerData>(() => tickerIndexes.value)
const isAccountMenuOpen = ref(false)
const accountMenuContainer = ref(null)

const resumeAnimationDelay = debounce(() => {
  if (import.meta.server) return
  resumeAnimation()
}, 1000)

const handlePauseAnimation = () => {
  pauseAnimation()
  debounceResumeAnimation.clear()
}

const handleMoveToLeft = () => {
  moveToLeft()
  resumeAnimationDelay()
}

const handleMoveToRight = () => {
  moveToRight()
  resumeAnimationDelay()
}

const toggleAccountMenu = () => {
  isAccountMenuOpen.value = !isAccountMenuOpen.value
}

const closeAccountMenu = () => {
  isAccountMenuOpen.value = false
}

watch(
  [isFirstIndexesContainerMounted, isSecondIndexesContainerMounted, indexes],
  ([first, second, currentIndexes]) => {
    if (import.meta.server) return
    if (!first || !second || !currentIndexes || currentIndexes.length === 0)
      return
    nextTick(() => {
      startAnimation()
    })
  },
)

onTokenChange(() => {
  if (import.meta.server) return
  refreshTickerData()
})

onMounted(() => {
  if (
    isFirstIndexesContainerMounted.value &&
    isSecondIndexesContainerMounted.value &&
    indexes.value &&
    indexes.value.length > 0
  ) {
    nextTick(() => {
      startAnimation()
    })
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    cleanupAnimation()
    debounceResumeAnimation.clear()
  }
})

onClickOutside(accountMenuContainer, () => {
  closeAccountMenu()
})
</script>

<template>
  <div
    class="flex w-full items-center justify-center bg-s-900 px-0 py-2 text-white md:px-4 lg:pl-10 lg:pr-6 2xl:px-0"
  >
    <div
      class="relative flex max-w-content-container flex-1 items-center justify-between gap-2 mx-auto"
    >
      <div
        class="relative flex h-5 min-w-0 grow text-sm opacity-100 transition-opacity"
      >
        <button
          class="flex w-5 items-center justify-center"
          @click="handleMoveToRight"
        >
          <BaseIcon name="angle-left" />
        </button>
        <div
          id="ticker-container"
          class="relative flex grow overflow-hidden scroll-smooth pr-16 font-semibold"
          @mouseover="handlePauseAnimation"
          @mouseleave="resumeAnimationDelay"
        >
          <div class="indexes-container absolute flex items-center gap-8 pr-8">
            <TickerIndex
              v-for="index in indexes"
              :key="index.name"
              :index="index"
              @resume-animation="resumeAnimationDelay"
              @pause-animation="handlePauseAnimation"
              @mounted="isFirstIndexesContainerMounted = true"
            />
          </div>
          <div class="indexes-container absolute flex items-center gap-8 pr-8">
            <TickerIndex
              v-for="index in indexes"
              :key="index.name"
              :index="index"
              @resume-animation="resumeAnimation"
              @pause-animation="handlePauseAnimation"
              @mounted="isSecondIndexesContainerMounted = true"
            />
          </div>
        </div>

        <button
          class="flex w-5 items-center justify-center"
          @click="handleMoveToLeft"
        >
          <span class="transform rotate-180">
            <BaseIcon name="angle-left" />
          </span>
        </button>
      </div>
      <a
        class="font-bold text-base hidden lg:block"
        :href="portalUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        Portal
      </a>
      <div class="hidden lg:flex items-center justify-between gap-x-5">
        <div class="relative shrink-0 w-20 flex justify-end h-6">
          <div
            v-if="authChecked && isAuthenticated"
            ref="accountMenuContainer"
            class="relative"
          >
            <button
              data-testid="account-menu-trigger"
              class="font-bold text-base hidden lg:block"
              @click="toggleAccountMenu"
            >
              Account
            </button>
            <AccountMenu v-if="isAccountMenuOpen" @close="closeAccountMenu" />
          </div>
          <a
            v-else-if="authChecked"
            data-testid="login-link"
            class="font-bold text-base hidden lg:block"
            href="/login"
            @click.prevent="$modal.open('login')"
          >
            Sign In
          </a>
        </div>
      </div>
    </div>
  </div>
</template>
